import React, { useEffect } from 'react';
import {
  ApButtonSecondary,
  ApCheckbox,
  ApSimpleDropdown,
} from '@alixpartners/ui-components';

import {
  BREADCRUMBS_MAP,
  CHART_TYPES,
  CHART_TYPES_CONSTANTS,
  CONTRIBUTIONS,
  DISPLAY_BY,
  DISPLAY_BY_OPTIONS,
  EXPORT_OPTIONS,
  SPEND_BY_OPTIONS,
  getSpendContributionOptions,
  PERMISSIONS,
} from 'utils/constants';
import { IParams, ISelectOption, ISpendByType } from 'utils/types';
import QueryParams from 'context/QueryParams';
import {
  Container,
  Divider,
  Top,
  Bottom,
  BreadcrumbsContainer,
  SpaceBetweenDropdowns,
  Wrapper,
  CheckboxWrapper,
} from './ChartOptions.styles';
import { Breadcrumbs } from './Breadcrumbs';
import { ApiContext } from 'context/ApiContext';

export const ChartOptionsView = ({
  clearL1,
  clearL2,
  contributions,
  spendBy,
  selectedChartOption,
  displayBy,
  permissions,
  permanentContractorChecked,
  onPermanentContractorChange,
  onChartChange,
  onDisplayByChange,
  onSpendContributionChange,
  onSpendChange,
  onExport,
  params,
  selectedL1,
  selectedL2,
}: {
  clearL1: () => void;
  clearL2: () => void;
  contributions?: string;
  spendBy: ISpendByType;
  onSpendChange: (o: ISelectOption) => void;
  params?: IParams;
  selectedChartOption: string;
  onChartChange: (o: string) => void;
  displayBy: string;
  onDisplayByChange: (o: ISelectOption) => void;
  onSpendContributionChange: (o: ISelectOption) => void;
  permanentContractorChecked: boolean;
  permissions: string[];
  onPermanentContractorChange: (o: boolean) => void;
  onExport: () => {};
  selectedL1?: string;
  selectedL2?: string;
}) => {
  const [isHide, setIsHide] = React.useState<boolean>(false);
  const isPersonnelDisabled = !permissions.includes(PERMISSIONS.VIEW_PERSONNEL);
  const spendContributionsOptions =
    getSpendContributionOptions(isPersonnelDisabled);
  const isPersonnel = contributions === CONTRIBUTIONS.PERSONNEL;
  let selectedContribution =
    spendContributionsOptions[isPersonnelDisabled ? 1 : 0];
  if (contributions) {
    if (contributions.length === 2) {
      selectedContribution = spendContributionsOptions[0];
    } else {
      selectedContribution =
        isPersonnel && !isPersonnelDisabled
          ? spendContributionsOptions[2]
          : spendContributionsOptions[1];
    }
  }
  let breadcrumbs = [BREADCRUMBS_MAP[selectedContribution.value]];
  if (selectedL1) {
    breadcrumbs.push(selectedL1);
  }
  if (selectedL2) {
    breadcrumbs.push(selectedL2);
  }

  useEffect(() => {
    if (
      contributions !== CONTRIBUTIONS.PERSONNEL &&
      selectedChartOption === CHART_TYPES_CONSTANTS.MAP
    ) {
      onChartChange(CHART_TYPES[1].value);
    }

    if (
      displayBy === DISPLAY_BY.PERCENTAGE_OF_REVENUE &&
      selectedChartOption === CHART_TYPES_CONSTANTS.MAP
    ) {
      onDisplayByChange({
        label: DISPLAY_BY.COST,
        value: DISPLAY_BY.COST,
      });
    }

    if (
      selectedChartOption === CHART_TYPES_CONSTANTS.MAP &&
      spendBy !== ISpendByType.GEOGRAPHY
    ) {
      onSpendChange(SPEND_BY_OPTIONS[2]);
    }
  }, [
    contributions,
    params,
    displayBy,
    selectedChartOption,
    spendBy,
    permanentContractorChecked,
    onChartChange,
    onDisplayByChange,
    onSpendChange,
  ]);
  const selectedChartOptionDropdown = CHART_TYPES.find(
    (chart: ISelectOption) => chart.value === selectedChartOption
  );
  const displayByOption = {
    label: displayBy,
    value: displayBy,
  };
  const selectedSpenByLabel = SPEND_BY_OPTIONS.find(
    (option) => option.value === spendBy
  )?.label;
  const selectedSpendByOption = {
    label: selectedSpenByLabel || spendBy,
    value: spendBy,
  };

  return (
    <Container>
      <Top>
        <BreadcrumbsContainer>
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onClick={(id) => {
              if (id === 0) {
                clearL2();
                clearL1();
              }
              if (id === 1) {
                clearL2();
              }
            }}
          />
        </BreadcrumbsContainer>
        <ApButtonSecondary iconName="tune" onClick={() => setIsHide(!isHide)}>
          {isHide ? 'Show ' : 'Hide '}options
        </ApButtonSecondary>
      </Top>
      <Bottom isHide={isHide}>
        <Wrapper>
          <ApSimpleDropdown
            onChange={(contributions) => {
              onSpendContributionChange(contributions as ISelectOption);
            }}
            options={spendContributionsOptions}
            selected={selectedContribution}
          />

          <Divider />

          {selectedChartOption !== CHART_TYPES_CONSTANTS.MAP && (
            <ApSimpleDropdown
              onChange={(option) => onSpendChange(option as ISelectOption)}
              options={SPEND_BY_OPTIONS.map((option) => ({
                ...option,
                disabled: option.value === ISpendByType.VENDORS && isPersonnel,
              }))}
              selected={selectedSpendByOption}
            />
          )}

          <SpaceBetweenDropdowns />

          <ApSimpleDropdown
            onChange={(option) => onDisplayByChange(option as ISelectOption)}
            options={DISPLAY_BY_OPTIONS.map((option) => ({
              ...option,
              disabled:
                (option.value === DISPLAY_BY.HEADCOUNT && !isPersonnel) ||
                (option.value === DISPLAY_BY.PERCENTAGE_OF_REVENUE &&
                  selectedChartOption === CHART_TYPES_CONSTANTS.MAP),
            }))}
            selected={displayByOption}
          />

          <Divider />

          {isPersonnel && (
            <CheckboxWrapper>
              <ApCheckbox
                id="select-is-permanent-contract"
                checked={permanentContractorChecked}
                onChange={() =>
                  onPermanentContractorChange(!permanentContractorChecked)
                }
              >
                Permanent/contractor split
              </ApCheckbox>
            </CheckboxWrapper>
          )}
        </Wrapper>

        <Wrapper>
          <ApSimpleDropdown
            iconName={selectedChartOptionDropdown?.icon}
            onChange={(option) => {
              // @ts-ignore
              onChartChange(option as ISelectOption);
            }}
            options={CHART_TYPES.map((option) => ({
              ...option,
              disabled:
                option.value === CHART_TYPES_CONSTANTS.MAP && !isPersonnel,
            }))}
            selected={selectedChartOptionDropdown}
          />

          <Divider />

          <ApSimpleDropdown
            iconName="outline_save_alt"
            onChange={onExport}
            options={EXPORT_OPTIONS}
            label="Export"
            disabled={selectedChartOption === CHART_TYPES_CONSTANTS.MAP}
          />
        </Wrapper>
      </Bottom>
    </Container>
  );
};

export default (props: any) => (
  <ApiContext.Consumer>
    {({ permissions }) => (
      <QueryParams>
        {({ params }) => (
          <ChartOptionsView
            {...props}
            params={params}
            permissions={permissions}
          />
        )}
      </QueryParams>
    )}
  </ApiContext.Consumer>
);
